import React, { FC, useState, useEffect } from 'react';
import {
  LinkMetadata,
  getInitialLinkMetadata,
  subscribeLinkMetadata,
} from '../../utils/linkMetadata';
import { ReactComponent as MediaLinksYoutubeIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/media-links/media-links-video-youtube-link.svg';
import { ReactComponent as MediaLinksZoomIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/media-links/media-links-zoom-link.svg';
import { ReactComponent as MediaLinksGeneralIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/media-links/media-links-general-link.svg';
import './ChatLinkCard.scss';

export interface ChatLinkCardProps {
  url: string;
  contextText?: string;
  initialTitle?: string;
  initialDescription?: string;
  className?: string;
  variant?: 'message' | 'list';
  isSentByTrainer?: boolean;
}

export const ChatLinkCard: FC<ChatLinkCardProps> = ({
  url,
  contextText,
  initialTitle,
  initialDescription,
  className = '',
  variant = 'message',
  isSentByTrainer = false,
}) => {
  const [meta, setMeta] = useState<LinkMetadata>(() => {
    const init = getInitialLinkMetadata(url, contextText);
    if (initialTitle) init.title = initialTitle;
    if (initialDescription) init.description = initialDescription;
    return init;
  });

  useEffect(() => {
    // Zoom links always use the Figma copy directly
    if (meta.type === 'zoom') {
      return;
    }

    // Subscribe to dynamic client-side metadata updates
    const unsubscribe = subscribeLinkMetadata(url, (dynamicMeta) => {
      setMeta((prev) => ({
        ...prev,
        title: dynamicMeta.title || prev.title,
        description: dynamicMeta.description || prev.description,
        image: dynamicMeta.image || prev.image,
        type: (dynamicMeta.type as any) || prev.type,
      }));
    });

    return () => unsubscribe();
  }, [url, meta.type]);

  const getLinkIcon = () => {
    if (meta.type === 'zoom') {
      return <MediaLinksZoomIcon className="link-type-svg" />;
    }
    if (meta.type === 'youtube') {
      return <MediaLinksYoutubeIcon className="link-type-svg" />;
    }
    return <MediaLinksGeneralIcon className="link-type-svg" />;
  };

  const handleOpenLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    const href = url.startsWith('http') ? url : `https://${url}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const themeClass = isSentByTrainer && variant === 'message' ? 'theme-sent' : 'theme-dark';

  return (
    <div
      className={`chat-link-card-container ${variant === 'list' ? 'variant-list' : 'variant-message'} ${themeClass} ${className}`}
      onClick={handleOpenLink}
      role="button"
      tabIndex={0}
    >
      <div className={`link-card-icon-box icon-${meta.type}`}>{getLinkIcon()}</div>

      <div className="link-card-content">
        <h4 className="link-card-title" title={meta.title}>
          {meta.title}
        </h4>
        {meta.description && (
          <p className="link-card-description" title={meta.description}>
            {meta.description}
          </p>
        )}
        <span className="link-card-domain" title={meta.url}>
          {meta.displayUrl}
        </span>
      </div>

      <button
        className="link-card-action-btn"
        onClick={handleOpenLink}
        title={`Open ${meta.displayUrl}`}
        aria-label={`Open ${meta.displayUrl}`}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="link-card-open-svg"
        >
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
      </button>
    </div>
  );
};
